import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

import { getZoom } from '@/lib/api/generated/zoom/zoom';
import { useToastStore } from '@/shared/store/toastStore';
import { useZoomConnectOverlayStore } from '@/shared/store/zoomConnectOverlayStore';
import { getApiErrorCode } from '@/shared/utils/queryUtils';

const { authorize, complete } = getZoom();

/**
 * /oauth/complete로 연결을 마무리할 때 쓰는 completionKey를 앱에만 보관하는 키.
 * 백엔드 요구사항: completionKey는 URL/딥링크에 절대 넣지 말고 클라이언트에만 들고 있을 것.
 * sessionStorage를 쓰는 이유: 인앱 브라우저(Browser.open())가 떠 있는 동안에도 앱 웹뷰 자체는
 * 계속 살아있어 모듈 변수로도 충분하지만, OAuth가 오래 걸려 OS가 백그라운드의 앱을 통째로
 * 종료시키는 경우까지 방어하기 위해 앱을 새로 열어도 이어받을 수 있게 저장해둔다.
 */
const COMPLETION_KEY_STORAGE_KEY = 'zoomCompletionKey';

/** useZoomConnectListener가 브라우저 종료 시점에 이어받을 completionKey. 없으면 이 리스너가 반응할 대상이 아니다. */
export function getPendingZoomCompletionKey(): string | null {
  return sessionStorage.getItem(COMPLETION_KEY_STORAGE_KEY);
}

export function clearPendingZoomCompletionKey() {
  sessionStorage.removeItem(COMPLETION_KEY_STORAGE_KEY);
}

/**
 * 콜백이 아직 도착하지 않았으면 백엔드가 409 ZOOM_AUTHORIZATION_PENDING을 주는데(같은 키로 재시도 가능),
 * 웹 팝업 폴링(watchWebZoomPopup)이 이 간격/횟수를 쓴다(2초 × 150회 ≈ 5분, completionKey의 서버측
 * 만료 시간(10분)보다 충분히 짧게). 네이티브(useZoomConnectListener)는 같은 값을 쓰지만 별도로
 * 자기 것을 들고 있다 — 두 흐름을 억지로 묶기보다 독립적으로 두는 쪽이 안전하다.
 */
const ZOOM_COMPLETE_RETRY_DELAY_MS = 2000;
const ZOOM_COMPLETE_MAX_RETRIES = 150;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type ZoomCompleteOutcome = 'connected' | 'pending' | 'failed';

export interface ZoomCompleteResult {
  outcome: ZoomCompleteOutcome;
  /** outcome이 'failed'일 때 서버 에러 코드 (예: ZOOM_INVALID_STATE, ZOOM_AUTHORIZATION_FAILED) */
  errorCode?: string;
}

/**
 * /oauth/complete 1회 호출.
 * 콜백이 아직 도착하지 않았으면 백엔드가 409 ZOOM_AUTHORIZATION_PENDING을 주는데(같은 키로 재시도 가능),
 * 이건 실패가 아니라 "아직"이라 'pending'으로 구분해서 반환한다 — 호출부가 잠깐 뒤 재시도할지 판단하는
 * 근거로 쓴다. 그 외(만료·다른 사용자·소비된 키(ZOOM_INVALID_STATE)·사용자 거절(ZOOM_AUTHORIZATION_FAILED)
 * 등)는 재시도해도 소용없는 최종 실패라 'failed'로 묶고, 에러 코드를 같이 반환해 호출부가 더 구체적인
 * 안내 문구를 고를 수 있게 한다.
 */
export async function completeZoomConnect(completionKey: string): Promise<ZoomCompleteResult> {
  try {
    const res = await complete({ completionKey });
    return { outcome: res.success && res.data?.connected ? 'connected' : 'failed' };
  } catch (error) {
    const code = getApiErrorCode(error);
    if (code === 'ZOOM_AUTHORIZATION_PENDING') return { outcome: 'pending' };
    return { outcome: 'failed', errorCode: code ?? undefined };
  }
}

/**
 * 웹 팝업 흐름이 최종 상태('connected'|'failed')로 끝났을 때 토스트를 띄운다. 캐시는 여기서
 * 직접 무효화하지 않는다 — 이 앱은 refetchOnWindowFocus가 기본 켜져 있어서, 팝업이 닫히고
 * 포커스가 이 탭으로 돌아오는 순간 화면에 떠 있는 관련 쿼리(zoomConnection 등)가 자동으로
 * 다시 조회된다. 호출부가 openZoomConnect()를 await해서 완료를 보장받고 싶으면(예:
 * admin/attendance/zoom-account 페이지) 자기 쿼리 키를 직접 invalidate하면 된다.
 */
function finalizeWebZoomConnect(outcome: 'connected' | 'failed', errorCode?: string) {
  clearPendingZoomCompletionKey();

  if (outcome === 'connected') {
    useToastStore.getState().showToast('success', 'Zoom 계정이 연결됐어요.', '5rem');
    return;
  }

  const message =
    errorCode === 'ZOOM_INVALID_STATE'
      ? '연결 요청이 만료됐거나 이미 사용됐어요. 다시 시도해주세요.'
      : errorCode === 'ZOOM_AUTHORIZATION_FAILED'
        ? 'Zoom 인증이 거절됐어요. 다시 시도해주세요.'
        : 'Zoom 연결에 실패했어요. 다시 시도해주세요.';
  useToastStore.getState().showToast('error', message, '5rem');
}

/** 백엔드 zoom.ts의 callback 엔드포인트 경로 — Zoom이 여기로 리다이렉트하면 popup을 우리 쪽이 넘겨받는다. */
const OAUTH_CALLBACK_PATH = '/api/integrations/zoom/oauth/callback';
const LOCATION_CHECK_INTERVAL_MS = 250;

function isSameOriginUrl(url: string): boolean {
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * 웹은 @capacitor/browser의 web 구현체가 window.open()만 하고 끝나 browserPageLoaded/browserFinished
 * 이벤트를 전혀 쏴주지 않는다(네이티브 전용). 그래서 네이티브가 그 이벤트를 트리거로 폴링을 시작하는 것과
 * 달리, 웹에서는 팝업을 연 직후부터 이 함수가 직접 두 가지를 병행한다:
 *
 * 1. (빠른 감시) popup.location을 250ms마다 확인해서 Zoom 도메인(크로스오리진)을 벗어나 우리 쪽
 *    /oauth/callback(동일 origin)에 도착하는 순간을 잡아 즉시 popup을 닫는다 — 백엔드가 그 경로에
 *    JSON을 그대로 내려주기 때문에, 사용자가 그 못생긴 화면을 보는 시간을 최소화하기 위함이다.
 *    Zoom 페이지에 있는 동안은 크로스오리진이라 location 접근 자체가 예외를 던지는데, 이건
 *    정상 상황이라 조용히 무시한다.
 * 2. (느린 폴링) /oauth/complete를 2초 간격으로 호출해 실제 연결 완료를 확인한다. 콜백 도착을
 *    감지한 즉시도 한 번 더 확인해서, 다음 정기 폴링 틱까지 기다리지 않고 최대한 빨리 결과를 반영한다.
 *
 * 사용자가 콜백에 도달하기 전에 팝업을 직접 닫아버린 경우(취소)도 마지막으로 한 번 더 확인 후 정리한다.
 * 로딩 오버레이는 openZoomConnect가 클릭 시점부터 이 함수가 끝날 때까지 통째로 들고 있으므로
 * 여기서는 건드리지 않는다.
 */
async function watchWebZoomPopup(popup: Window, completionKey: string) {
  let settled = false;
  let landedOnCallback = false;

  const finishOnce = (outcome: 'connected' | 'failed', errorCode?: string) => {
    if (settled) return;
    settled = true;
    if (!popup.closed) popup.close();
    finalizeWebZoomConnect(outcome, errorCode);
  };

  const locationWatcher = window.setInterval(() => {
    if (settled || popup.closed) return;
    try {
      const href = popup.location.href;
      if (!landedOnCallback && isSameOriginUrl(href) && href.includes(OAUTH_CALLBACK_PATH)) {
        landedOnCallback = true;
        popup.close();
        completeZoomConnect(completionKey).then(({ outcome, errorCode }) => {
          if (outcome !== 'pending') finishOnce(outcome, errorCode);
        });
      }
    } catch {
      // 아직 Zoom(크로스오리진) 페이지에 있어 location 접근이 막힌 것 — 정상 상황이므로 무시한다.
    }
  }, LOCATION_CHECK_INTERVAL_MS);

  try {
    for (let attempt = 0; attempt < ZOOM_COMPLETE_MAX_RETRIES && !settled; attempt++) {
      const { outcome, errorCode } = await completeZoomConnect(completionKey);
      if (settled) return;

      if (outcome !== 'pending') {
        finishOnce(outcome, errorCode);
        return;
      }

      if (popup.closed && !landedOnCallback) {
        const final = await completeZoomConnect(completionKey);
        finishOnce(final.outcome === 'connected' ? 'connected' : 'failed', final.errorCode);
        return;
      }

      await wait(ZOOM_COMPLETE_RETRY_DELAY_MS);
    }

    if (!settled) {
      clearPendingZoomCompletionKey();
      useToastStore.getState().showToast('error', 'Zoom 연결 확인이 지연되고 있어요. 다시 시도해주세요.', '5rem');
    }
  } finally {
    window.clearInterval(locationWatcher);
  }
}

/**
 * Zoom 계정 연결 시작.
 * - 네이티브: 백엔드가 발급한 Zoom 동의 URL을 인앱 브라우저로 열고, 완료 감지는 useZoomConnectListener의
 *   browserPageLoaded/browserFinished 이벤트가 처리한다. 인앱 브라우저 자체가 화면을 통째로 가리므로
 *   별도 오버레이는 필요 없다.
 * - 웹: Browser 플러그인이 popup만 띄우고 생명주기 이벤트를 주지 않으므로 window.open()으로 직접 열고
 *   watchWebZoomPopup이 완료를 감지·마무리한다. 클릭 제스처와 window.open() 사이에 authorize() 네트워크
 *   요청이 끼면 일부 브라우저(Safari 등)가 팝업을 차단할 수 있어, 먼저 빈 팝업을 동기적으로 연 뒤 URL을
 *   채워 넣는 순서로 한다. 클릭 직후부터 전체 흐름이 끝날 때까지 Zoom 전용 오버레이(앱 전체가 공유하는
 *   로딩 오버레이와는 분리된 별도 상태)를 계속 띄워두는데, 화면 전체를 덮어 포인터 이벤트를 막기 때문에
 *   "연결" 버튼을 빠르게 두 번 눌러도 두 번째 클릭이 버튼에 도달하지 못해 팝업이 중복으로 뜨지 않는다.
 *
 * 쿠키 없는 방식: authorize()가 authorizationUrl과 함께 completionKey를 내려준다. 예전엔 authorize()
 * 호출(앱 웹뷰)에서 심은 쿠키를 콜백(인앱 브라우저/팝업)에서 검증했는데, iOS SFSafariViewController·
 * Android Custom Tabs·웹 팝업 모두 앱 웹뷰와 쿠키 저장소가 분리돼 있어 항상 실패할 구조였다. 지금은
 * completionKey를 앱에서만 들고 있다가 브라우저가 닫힌 뒤 /oauth/complete로 직접 제시하는 방식이라
 * 쿠키가 아예 필요 없다.
 */
export async function openZoomConnect() {
  const isNative = Capacitor.isNativePlatform();

  if (isNative) {
    try {
      const res = await authorize();
      if (!res.success || !res.data?.authorizationUrl || !res.data.completionKey) {
        throw new Error(res.error?.message ?? 'Zoom 연결 URL을 가져오지 못했습니다.');
      }
      sessionStorage.setItem(COMPLETION_KEY_STORAGE_KEY, res.data.completionKey);
      await Browser.open({ url: res.data.authorizationUrl });
    } catch {
      clearPendingZoomCompletionKey();
      useToastStore.getState().showToast('error', 'Zoom 연결을 시작하지 못했어요. 다시 시도해주세요.', '5rem');
    }
    return;
  }

  // 클릭과 동시에(동기적으로) 오버레이를 켜야 팝업 차단 회피용 window.open()과 마찬가지로
  // 두 번째 클릭이 버튼에 닿기 전에 화면을 막을 수 있다.
  useZoomConnectOverlayStore.getState().setIsVisible(true);

  try {
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      useToastStore.getState().showToast('error', '팝업이 차단됐어요. 브라우저의 팝업 차단을 해제하고 다시 시도해주세요.', '5rem');
      return;
    }

    try {
      const res = await authorize();
      if (!res.success || !res.data?.authorizationUrl || !res.data.completionKey) {
        throw new Error(res.error?.message ?? 'Zoom 연결 URL을 가져오지 못했습니다.');
      }

      sessionStorage.setItem(COMPLETION_KEY_STORAGE_KEY, res.data.completionKey);
      popup.location.href = res.data.authorizationUrl;

      // 팝업이 닫히고 연결 성공/실패가 확정될 때까지 기다린다 — 그래야 아래 finally가
      // 그 시점에 맞춰 오버레이를 끈다.
      await watchWebZoomPopup(popup, res.data.completionKey);
    } catch {
      popup.close();
      clearPendingZoomCompletionKey();
      useToastStore.getState().showToast('error', 'Zoom 연결을 시작하지 못했어요. 다시 시도해주세요.', '5rem');
    }
  } finally {
    useZoomConnectOverlayStore.getState().setIsVisible(false);
  }
}
